import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { ArrowLeft, Upload, Check, Save } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

const TicketDesignerSimple = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [selectedPlans, setSelectedPlans] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('ticket');
  const [selectedPlanForPreview, setSelectedPlanForPreview] = useState(null);
  const [previewLogoUrl, setPreviewLogoUrl] = useState(null);
  
  // Estados para configuración de escala
  const [ticketConfig, setTicketConfig] = useState({
    scale: 1.25,
    vertical_offset: -1.5,
    top_margin: 1.5
  });
  
  const [cartaConfig, setCartaConfig] = useState({
    scale: 1.5,
    cell_width: 40.0,
    cell_height: 70.0,
    vertical_offset: -0.5,
    top_margin: 6.5
  });

  useEffect(() => {
    loadPlans();
    loadConfig();
  }, []);

  useEffect(() => {
    if (selectedPlanForPreview && plans.length > 0) {
      const plan = plans.find(p => p.id === selectedPlanForPreview);
      setPreviewLogoUrl(plan?.logo_url || null);
    }
  }, [selectedPlanForPreview, plans]);

  const loadPlans = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/plans`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      setPlans(data);
      
      if (data.length > 0) {
        const planWithLogo = data.find(p => p.logo_url);
        setSelectedPlanForPreview(planWithLogo?.id || data[0].id);
        setPreviewLogoUrl(planWithLogo?.logo_url || null);
      }
    } catch (err) {
      console.error('Error cargando planes:', err);
      setError('Error al cargar planes');
    } finally {
      setLoading(false);
    }
  };

  const loadConfig = async () => {
    try {
      const ticketResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/design/ticket`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const cartaResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/design/carta`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (ticketResponse.ok) {
        const ticketData = await ticketResponse.json();
        setTicketConfig({
          scale: ticketData.ticket_scale || 1.0,
          vertical_offset: ticketData.ticket_vertical_offset || 0.0,
          top_margin: ticketData.ticket_top_margin || 0.0
        });
      }
      
      if (cartaResponse.ok) {
        const cartaData = await cartaResponse.json();
        setCartaConfig({
          scale: cartaData.carta_scale || 1.0,
          cell_width: cartaData.carta_cell_width || 42.0,
          cell_height: cartaData.carta_cell_height || 74.25,
          vertical_offset: cartaData.carta_vertical_offset || 0.0,
          top_margin: cartaData.carta_top_margin || 0.0
        });
      }
    } catch (err) {
      console.error('Error cargando configuración:', err);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Por favor selecciona una imagen válida');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setError('La imagen no debe superar 2MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setLogoPreview(event.target.result);
      setShowModal(true);
      setSelectedPlans([]);
      setError('');
    };
    reader.readAsDataURL(file);
  };

  const togglePlanSelection = (planId) => {
    setSelectedPlans(prev => 
      prev.includes(planId) 
        ? prev.filter(id => id !== planId)
        : [...prev, planId]
    );
  };

  const selectAllPlans = () => {
    setSelectedPlans(plans.map(p => p.id));
  };

  const deselectAllPlans = () => {
    setSelectedPlans([]);
  };

  const handleApplyLogo = async () => {
    if (selectedPlans.length === 0) {
      setError('Selecciona al menos un plan');
      return;
    }

    setUploading(true);
    setError('');
    
    try {
      for (const planId of selectedPlans) {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/plans/${planId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            logo_url: logoPreview
          })
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Error actualizando plan');
        }
      }
      
      setSuccess(`✅ Logo aplicado a ${selectedPlans.length} plan(es)`);
      setShowModal(false);
      setLogoPreview(null);
      await loadPlans();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error aplicando logo:', err);
      setError('Error al aplicar el logo');
    } finally {
      setUploading(false);
    }
  };

  const handlePreview = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/design/preview-pdf?size=${activeTab === 'ticket' ? 'ticket' : 'a4'}${selectedPlanForPreview ? `&plan_id=${selectedPlanForPreview}` : ''}`;
    fetch(url, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    })
    .then(response => response.blob())
    .then(blob => {
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
    })
    .catch(err => {
      console.error('Error:', err);
      setError('Error al generar vista previa');
    });
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    setError('');
    
    try {
      if (activeTab === 'ticket') {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/design/ticket`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            ticket_scale: ticketConfig.scale,
            ticket_vertical_offset: ticketConfig.vertical_offset,
            ticket_top_margin: ticketConfig.top_margin
          })
        });
      } else {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/design/carta`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            carta_scale: cartaConfig.scale,
            carta_cell_width: cartaConfig.cell_width,
            carta_cell_height: cartaConfig.cell_height,
            carta_vertical_offset: cartaConfig.vertical_offset,
            carta_top_margin: cartaConfig.top_margin
          })
        });
      }
      
      setSuccess('✅ Ajustes guardados correctamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error guardando configuración:', err);
      setError('Error al guardar ajustes');
    } finally {
      setSaving(false);
    }
  };

  // Generar HTML de vista previa en tiempo real
  const generateLivePreview = () => {
    const config = activeTab === 'ticket' ? ticketConfig : cartaConfig;
    
    if (activeTab === 'ticket') {
      // Vista previa de 1 ticket
      const scale = config.scale;
      const offset = config.vertical_offset;
      const logoHeight = 12 * scale;
      const qrSize = 30 * scale;
      const credFont = 8 * scale;
      const posFont = 8 * scale;
      const priceFont = 14 * scale;
      const planFont = 10 * scale;
      
      return `
        <div style="
          width: 80mm;
          min-height: 120mm;
          background: white;
          border: 2px solid #ccc;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: flex-start;
          padding-top: ${5 + offset}mm;
          padding-left: 5mm;
          padding-right: 5mm;
          padding-bottom: 5mm;
          box-sizing: border-box;
          font-family: Arial, sans-serif;
        ">
          <div style="width: 100%; height: 2px; background: #666; margin: 3mm 0;"></div>
          
          ${previewLogoUrl ? `
            <img src="${previewLogoUrl}" style="
              max-width: 60mm;
              max-height: ${logoHeight}mm;
              object-fit: contain;
              margin: 3mm auto;
              display: block;
            " />
          ` : `<div style="height: ${logoHeight}mm;"></div>`}
          
          <div style="
            width: ${qrSize}mm;
            height: ${qrSize}mm;
            background: #f0f0f0;
            border: 2px dashed #999;
            margin: 3mm auto;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 10px;
            color: #666;
          ">QR</div>
          
          <div style="text-align: center; margin: 3mm 0;">
            <div style="font-size: ${credFont}pt; font-weight: bold;">User: DEMO123</div>
            <div style="font-size: ${credFont}pt; font-weight: bold;">Pass: pass123</div>
          </div>
          
          ${previewLogoUrl ? `<div style="font-size: ${posFont}pt; font-weight: bold; margin: 2mm 0;">PUNTO VENTA DEMO</div>` : ''}
          
          <div style="width: 100%; height: 2px; background: #666; margin: 3mm 0;"></div>
          
          <div style="font-size: ${planFont}pt; font-weight: bold; text-align: center; margin: 2mm 0;">Plan Demo</div>
          
          <div style="font-size: ${priceFont}pt; font-weight: bold; margin: 2mm 0;">$50</div>
          
          <div style="width: 100%; height: 2px; background: #666; margin: 3mm 0;"></div>
        </div>
      `;
    } else {
      // Vista previa de grid carta (4 fichas de muestra)
      const scale = config.scale;
      const cellWidth = config.cell_width;
      const cellHeight = config.cell_height;
      const offset = config.vertical_offset;
      const logoHeight = 8 * scale;
      const qrSize = 18 * scale;
      const credFont = 5 * scale;
      const posFont = 5.5 * scale;
      const priceFont = 9 * scale;
      
      const cellStyle = `
        width: ${cellWidth}mm;
        height: ${cellHeight}mm;
        border: 1px solid #000;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-start;
        padding-top: ${1 + offset}mm;
        padding-left: 0.5mm;
        padding-right: 0.5mm;
        padding-bottom: 1mm;
        overflow: hidden;
        font-family: Arial, sans-serif;
      `;
      
      const renderCell = (num) => `
        <div style="${cellStyle}">
          ${previewLogoUrl ? `
            <img src="${previewLogoUrl}" style="
              max-width: 90%;
              max-height: ${logoHeight}mm;
              object-fit: contain;
              margin: 0 auto 0.5mm auto;
              display: block;
            " />
          ` : `<div style="height: ${logoHeight}mm;"></div>`}
          
          <div style="
            width: ${qrSize}mm;
            height: ${qrSize}mm;
            background: #f0f0f0;
            border: 1px dashed #999;
            margin: 0.5mm auto;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 8px;
            color: #666;
          ">QR</div>
          
          <div style="font-size: ${credFont}pt; text-align: center; margin: 0.5mm 0; line-height: 1.2;">
            User: D${num}<br>Pass: p${num}
          </div>
          
          ${previewLogoUrl ? `<div style="font-size: ${posFont}pt; font-weight: bold; text-align: center; margin: 0.5mm 0;">POS DEMO</div>` : ''}
          
          <div style="font-size: ${priceFont}pt; font-weight: bold; text-align: center; margin: 0.5mm 0;">$50</div>
        </div>
      `;
      
      // Generar 20 celdas (5 columnas x 4 filas)
      let cells = '';
      for (let i = 1; i <= 20; i++) {
        cells += renderCell(i);
      }
      
      return `
        <div style="
          display: grid;
          grid-template-columns: repeat(5, ${cellWidth}mm);
          gap: 0;
          background: white;
          border: 2px solid #ccc;
          width: fit-content;
        ">
          ${cells}
        </div>
      `;
    }
  };

  const currentConfig = activeTab === 'ticket' ? ticketConfig : cartaConfig;
  const setCurrentConfig = activeTab === 'ticket' ? setTicketConfig : setCartaConfig;

  // Componente de vista previa de ticket
  const TicketPreview = () => {
    const scale = ticketConfig.scale;
    const offset = ticketConfig.vertical_offset;
    const topMargin = ticketConfig.top_margin;
    const logoHeight = 12 * scale;
    const qrSize = 30 * scale;
    const credFont = 8 * scale;
    const posFont = 8 * scale;
    const priceFont = 14 * scale;
    const planFont = 10 * scale;
    
    const selectedPlan = plans.find(p => p.id === selectedPlanForPreview) || plans[0];
    
    // Datos de ejemplo realistas
    const username = 'DEMO001';
    const password = 'pass001';
    const planName = selectedPlan?.name || 'Plan Demo';
    const price = selectedPlan?.price || 50;
    const posName = 'PUNTO VENTA DEMO';
    const qrData = `User: ${username}\nPass: ${password}`;
    
    return (
      <div style={{
        width: '80mm',
        minHeight: '120mm',
        background: 'white',
        border: '2px solid #ccc',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        paddingTop: `${5}mm`,
        paddingLeft: '5mm',
        paddingRight: '5mm',
        paddingBottom: '5mm',
        boxSizing: 'border-box',
        fontFamily: 'Arial, sans-serif'
      }}>
        {/* Margen superior antes de todo */}
        {topMargin > 0 && <div style={{ height: `${topMargin}mm` }}></div>}
        
        <div style={{ width: '100%', height: '2px', background: '#666', margin: `${3 + offset}mm 0` }}></div>
        
        {previewLogoUrl ? (
          <img src={previewLogoUrl} style={{
            maxWidth: '60mm',
            maxHeight: `${logoHeight}mm`,
            objectFit: 'contain',
            margin: `${3 + offset}mm auto`,
            display: 'block'
          }} alt="Logo" />
        ) : (
          <div style={{ height: `${logoHeight}mm`, margin: `${3 + offset}mm 0` }}></div>
        )}
        
        <div style={{ margin: `${3 + offset}mm auto` }}>
          <QRCodeSVG value={qrData} size={qrSize * 3.78} level="M" />
        </div>
        
        <div style={{ textAlign: 'center', margin: `${3 + offset}mm 0`, lineHeight: '1.4' }}>
          <div style={{ fontSize: `${credFont}pt`, fontWeight: 'bold', color: '#000' }}>User: {username}</div>
          <div style={{ fontSize: `${credFont}pt`, fontWeight: 'bold', color: '#000' }}>Pass: {password}</div>
        </div>
        
        <div style={{ fontSize: `${posFont}pt`, fontWeight: 'bold', margin: `${2 + offset}mm 0`, color: '#000' }}>
          {posName}
        </div>
        
        <div style={{ width: '100%', height: '2px', background: '#666', margin: `${3 + offset}mm 0` }}></div>
        
        <div style={{ fontSize: `${planFont}pt`, fontWeight: 'bold', textAlign: 'center', margin: `${2 + offset}mm 0`, color: '#000' }}>
          {planName}
        </div>
        
        <div style={{ fontSize: `${priceFont}pt`, fontWeight: 'bold', margin: `${2 + offset}mm 0`, color: '#000' }}>
          ${price}
        </div>
        
        <div style={{ width: '100%', height: '2px', background: '#666', margin: `${3 + offset}mm 0` }}></div>
      </div>
    );
  };

  // Componente de celda para carta
  const CartaCell = ({ num }) => {
    const scale = cartaConfig.scale;
    const cellWidth = cartaConfig.cell_width;
    const cellHeight = cartaConfig.cell_height;
    const offset = cartaConfig.vertical_offset;
    const topMargin = cartaConfig.top_margin;
    const logoHeight = 8 * scale;
    const qrSize = 18 * scale;
    const credFont = 5 * scale;
    const posFont = 5.5 * scale;
    const priceFont = 9 * scale;
    
    const selectedPlan = plans.find(p => p.id === selectedPlanForPreview) || plans[0];
    
    // Datos de ejemplo realistas
    const username = `DEMO${String(num).padStart(3, '0')}`;
    const password = `pass${String(num).padStart(3, '0')}`;
    const planName = selectedPlan?.name || 'Plan Demo';
    const price = selectedPlan?.price || 50;
    const posName = 'POS DEMO';
    const qrData = `User: ${username}\nPass: ${password}`;
    
    return (
      <div style={{
        width: `${cellWidth}mm`,
        height: `${cellHeight}mm`,
        border: '1px solid #000',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        paddingTop: `${1}mm`,
        paddingLeft: '0.5mm',
        paddingRight: '0.5mm',
        paddingBottom: '1mm',
        overflow: 'hidden',
        fontFamily: 'Arial, sans-serif'
      }}>
        {/* Margen superior antes de todo */}
        {topMargin > 0 && <div style={{ height: `${topMargin}mm`, flexShrink: 0 }}></div>}
        
        {previewLogoUrl ? (
          <img src={previewLogoUrl} style={{
            maxWidth: '90%',
            maxHeight: `${logoHeight}mm`,
            objectFit: 'contain',
            margin: `0 auto ${0.5 + offset}mm auto`,
            display: 'block',
            flexShrink: 0
          }} alt="Logo" />
        ) : (
          <div style={{ height: `${logoHeight}mm`, margin: `0 0 ${0.5 + offset}mm 0`, flexShrink: 0 }}></div>
        )}
        
        <div style={{ margin: `${0.5 + offset}mm auto`, flexShrink: 0 }}>
          <QRCodeSVG value={qrData} size={qrSize * 3.78} level="L" />
        </div>
        
        <div style={{ fontSize: `${credFont}pt`, textAlign: 'center', margin: `${0.5 + offset}mm 0`, lineHeight: '1.2', color: '#000', flexShrink: 0 }}>
          User: {username}<br />Pass: {password}
        </div>
        
        <div style={{ fontSize: `${posFont}pt`, fontWeight: 'bold', textAlign: 'center', margin: `${0.5 + offset}mm 0`, color: '#000', flexShrink: 0 }}>
          {posName}
        </div>
        
        <div style={{ fontSize: `${priceFont}pt`, fontWeight: 'bold', textAlign: 'center', margin: `${0.5 + offset}mm 0 0 0`, color: '#000', flexShrink: 0 }}>
          ${price}
        </div>
      </div>
    );
  };

  // Vista previa de carta con grid completo
  const CartaPreview = () => {
    const cells = [];
    for (let i = 1; i <= 20; i++) {
      cells.push(<CartaCell key={i} num={i} />);
    }
    
    return (
      <div style={{
        display: 'grid',
        gridTemplateColumns: `repeat(5, ${cartaConfig.cell_width}mm)`,
        gap: 0,
        background: 'white',
        border: '2px solid #ccc',
        width: 'fit-content'
      }}>
        {cells}
      </div>
    );
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} shadow-md`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Diseñador de Fichas
              </h1>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Configura logos y ajusta diseño
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Mensajes */}
        {success && (
          <div className="mb-4 p-4 bg-green-100 text-green-800 rounded-lg">
            {success}
          </div>
        )}
        
        {error && (
          <div className="mb-4 p-4 bg-red-100 text-red-800 rounded-lg">
            {error}
          </div>
        )}

        {/* Sección de Logo */}
        <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-6 mb-6`}>
          <h2 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Subir Logo
          </h2>
          
          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex-1">
              <div className="text-center py-8 border-2 border-dashed rounded-lg" style={{borderColor: isDark ? '#4B5563' : '#D1D5DB'}}>
                <Upload className={`mx-auto h-12 w-12 ${isDark ? 'text-gray-400' : 'text-gray-400'} mb-3`} />
                <p className={`text-sm mb-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  PNG, JPG hasta 2MB
                </p>
                
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <span className="inline-block px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                    Seleccionar Logo
                  </span>
                </label>
              </div>
            </div>

            <div className="flex-1">
              <h3 className={`text-sm font-semibold mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Logos Actuales
              </h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {plans.map(plan => (
                  <div
                    key={plan.id}
                    className={`flex items-center gap-3 p-2 rounded ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}
                  >
                    <div className={`w-12 h-12 ${isDark ? 'bg-gray-600' : 'bg-gray-200'} rounded flex items-center justify-center overflow-hidden flex-shrink-0`}>
                      {plan.logo_url ? (
                        <img src={plan.logo_url} alt={plan.name} className="max-w-full max-h-full object-contain" />
                      ) : (
                        <span className="text-xs text-gray-500">Sin logo</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {plan.name}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs de Configuración */}
        <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md overflow-hidden`}>
          {/* Tabs Header */}
          <div className={`flex border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <button
              onClick={() => setActiveTab('ticket')}
              className={`flex-1 px-6 py-4 text-sm font-medium ${
                activeTab === 'ticket'
                  ? isDark ? 'bg-gray-700 text-white border-b-2 border-blue-500' : 'bg-gray-50 text-gray-900 border-b-2 border-blue-600'
                  : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              🎫 Ticket Térmico
            </button>
            <button
              onClick={() => setActiveTab('carta')}
              className={`flex-1 px-6 py-4 text-sm font-medium ${
                activeTab === 'carta'
                  ? isDark ? 'bg-gray-700 text-white border-b-2 border-blue-500' : 'bg-gray-50 text-gray-900 border-b-2 border-blue-600'
                  : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              📄 Tamaño Carta
            </button>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Columna Izquierda - Controles */}
              <div>
                <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Ajustar Diseño {activeTab === 'ticket' ? '(Ticket Térmico)' : '(Carta 20 fichas)'}
                </h3>

                <div className="space-y-6">
                  {activeTab === 'ticket' ? (
                    <>
                      {/* Escala General Ticket */}
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          🔍 Escala General: {(ticketConfig.scale * 100).toFixed(0)}%
                        </label>
                        <input
                          type="range"
                          min="0.7"
                          max="1.3"
                          step="0.05"
                          value={ticketConfig.scale}
                          onChange={(e) => setTicketConfig({...ticketConfig, scale: parseFloat(e.target.value)})}
                          className="w-full"
                        />
                        <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          Ajusta el tamaño de todos los elementos proporcionalmente
                        </p>
                      </div>

                      {/* Margen Superior */}
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          📏 Margen Superior: {ticketConfig.top_margin >= 0 ? '+' : ''}{ticketConfig.top_margin.toFixed(1)}mm
                        </label>
                        <input
                          type="range"
                          min="0"
                          max="15"
                          step="0.5"
                          value={ticketConfig.top_margin}
                          onChange={(e) => setTicketConfig({...ticketConfig, top_margin: parseFloat(e.target.value)})}
                          className="w-full"
                        />
                        <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          Espacio antes del logo - baja todo el contenido
                        </p>
                      </div>

                      {/* Espaciado Vertical Ticket */}
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          📏 Espaciado Entre Elementos: {ticketConfig.vertical_offset >= 0 ? '+' : ''}{ticketConfig.vertical_offset.toFixed(1)}mm
                        </label>
                        <input
                          type="range"
                          min="-3"
                          max="3"
                          step="0.5"
                          value={ticketConfig.vertical_offset}
                          onChange={(e) => setTicketConfig({...ticketConfig, vertical_offset: parseFloat(e.target.value)})}
                          className="w-full"
                        />
                        <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          Ajusta espacios entre elementos (compacto/espaciado)
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Botón de valores recomendados */}
                      <div className="mb-4">
                        <button
                          onClick={() => setCartaConfig({
                            scale: 1.0,
                            cell_width: 40.0,
                            cell_height: 70.0,
                            vertical_offset: -0.5,
                            top_margin: 0.5
                          })}
                          className={`px-4 py-2 text-sm rounded-lg ${isDark ? 'bg-purple-600 hover:bg-purple-700' : 'bg-purple-500 hover:bg-purple-600'} text-white transition-colors`}
                        >
                          ⚡ Usar Valores Óptimos (Máximo Aprovechamiento)
                        </button>
                      </div>
                      
                      {/* Escala General Carta */}
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          🔍 Escala General: {(cartaConfig.scale * 100).toFixed(0)}%
                        </label>
                        <input
                          type="range"
                          min="0.7"
                          max="1.3"
                          step="0.05"
                          value={cartaConfig.scale}
                          onChange={(e) => setCartaConfig({...cartaConfig, scale: parseFloat(e.target.value)})}
                          className="w-full"
                        />
                        <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          Ajusta el tamaño de todos los elementos proporcionalmente
                        </p>
                      </div>

                      {/* Margen Superior */}
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          📏 Margen Superior: {cartaConfig.top_margin >= 0 ? '+' : ''}{cartaConfig.top_margin.toFixed(1)}mm
                        </label>
                        <input
                          type="range"
                          min="0"
                          max="8"
                          step="0.25"
                          value={cartaConfig.top_margin}
                          onChange={(e) => setCartaConfig({...cartaConfig, top_margin: parseFloat(e.target.value)})}
                          className="w-full"
                        />
                        <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          Espacio antes del logo - baja todo el contenido para centrarlo
                        </p>
                      </div>

                      {/* Ancho de Celda */}
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          📐 Ancho de Celda: {cartaConfig.cell_width.toFixed(1)}mm
                        </label>
                        <input
                          type="range"
                          min="35"
                          max="52"
                          step="0.5"
                          value={cartaConfig.cell_width}
                          onChange={(e) => setCartaConfig({...cartaConfig, cell_width: parseFloat(e.target.value)})}
                          className="w-full"
                        />
                        <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          Más pequeño = aprovecha más espacio (5 columnas en 210mm)
                        </p>
                      </div>

                      {/* Alto de Celda */}
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          📐 Alto de Celda: {cartaConfig.cell_height.toFixed(1)}mm
                        </label>
                        <input
                          type="range"
                          min="55"
                          max="80"
                          step="0.5"
                          value={cartaConfig.cell_height}
                          onChange={(e) => setCartaConfig({...cartaConfig, cell_height: parseFloat(e.target.value)})}
                          className="w-full"
                        />
                        <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          Más pequeño = aprovecha más espacio (4 filas en 297mm)
                        </p>
                      </div>

                      {/* Espaciado Vertical Carta */}
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          📏 Espaciado Entre Elementos: {cartaConfig.vertical_offset >= 0 ? '+' : ''}{cartaConfig.vertical_offset.toFixed(2)}mm
                        </label>
                        <input
                          type="range"
                          min="-1.5"
                          max="1.5"
                          step="0.25"
                          value={cartaConfig.vertical_offset}
                          onChange={(e) => setCartaConfig({...cartaConfig, vertical_offset: parseFloat(e.target.value)})}
                          className="w-full"
                        />
                        <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          Ajusta espacios entre elementos (compacto/espaciado)
                        </p>
                      </div>
                    </>
                  )}
                </div>

                {/* Plan selector para preview */}
                {plans.length > 0 && (
                  <div className="mt-6">
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Plan para vista previa:
                    </label>
                    <select
                      value={selectedPlanForPreview || ''}
                      onChange={(e) => setSelectedPlanForPreview(e.target.value)}
                      className={`w-full px-3 py-2 rounded-lg ${isDark ? 'bg-gray-700 text-white' : 'bg-white text-gray-900'} border ${isDark ? 'border-gray-600' : 'border-gray-300'}`}
                    >
                      {plans.map(plan => (
                        <option key={plan.id} value={plan.id}>{plan.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Botones - Guardar arriba, Vista Previa abajo */}
                <div className="flex flex-col gap-3 mt-6">
                  <button
                    onClick={handleSaveConfig}
                    disabled={saving}
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                  >
                    <Save className="w-5 h-5" />
                    {saving ? 'Guardando...' : 'Guardar Ajustes'}
                  </button>
                  <button
                    onClick={handlePreview}
                    className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    📄 Ver Vista Previa
                  </button>
                </div>
              </div>

              {/* Columna Derecha - Vista Previa en Vivo */}
              <div>
                <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Vista Previa en Tiempo Real
                </h3>
                <div className={`${isDark ? 'bg-gray-700' : 'bg-gray-100'} rounded-lg p-4 flex items-center justify-center min-h-[500px] overflow-auto`}>
                  <div style={{ transform: activeTab === 'carta' ? 'scale(0.45)' : 'scale(1)', transformOrigin: 'top center' }}>
                    {activeTab === 'ticket' ? <TicketPreview /> : <CartaPreview />}
                  </div>
                </div>
                <p className={`text-xs mt-2 text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Esta vista se actualiza automáticamente al mover los sliders
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de selección de planes */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto`}>
            <div className="p-6">
              <h2 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Aplicar Logo a Planes
              </h2>
              
              <div className="mb-6">
                <p className={`text-sm mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Vista previa:
                </p>
                <div className="aspect-video bg-gray-100 rounded flex items-center justify-center max-h-40">
                  <img 
                    src={logoPreview} 
                    alt="Preview"
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
              </div>

              <div className="flex gap-2 mb-4">
                <button
                  onClick={selectAllPlans}
                  className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Seleccionar Todos
                </button>
                <button
                  onClick={deselectAllPlans}
                  className={`px-3 py-1 text-sm ${isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'} rounded`}
                >
                  Deseleccionar Todos
                </button>
              </div>

              <div className="space-y-2 mb-6">
                {plans.map(plan => (
                  <label
                    key={plan.id}
                    className={`flex items-center gap-3 p-3 rounded cursor-pointer ${
                      selectedPlans.includes(plan.id)
                        ? 'bg-blue-50 border-2 border-blue-500'
                        : isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedPlans.includes(plan.id)}
                      onChange={() => togglePlanSelection(plan.id)}
                      className="w-4 h-4"
                    />
                    <div className="flex-1">
                      <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {plan.name}
                      </div>
                    </div>
                    {selectedPlans.includes(plan.id) && (
                      <Check className="w-5 h-5 text-blue-600" />
                    )}
                  </label>
                ))}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleApplyLogo}
                  disabled={uploading || selectedPlans.length === 0}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploading ? 'Aplicando...' : `Aplicar a ${selectedPlans.length} plan(es)`}
                </button>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setLogoPreview(null);
                  }}
                  disabled={uploading}
                  className={`px-4 py-2 ${isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'} rounded-lg disabled:opacity-50`}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketDesignerSimple;
